import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api";

// ==================== Initial State ====================
const initialState = {
  healthSafetyForms: [],
  currentForm: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all Health Safety forms (optional patient filter)
export const fetchHealthSafetyForms = createAsyncThunk(
  "healthSafetyForm/fetchAll",
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId ? `/health-safety/${patientId}` : "/health-safety";
      const response = await api.get(url);
      return response.data?.data ?? response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch Health & Safety forsms"
      );
    }
  }
);

// Fetch single Health Safety form
export const fetchHealthSafetyForm = createAsyncThunk(
  "healthSafetyForm/fetchOne",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/health-safety/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch Health & Safety form"
      );
    }
  }
);

// Create Health Safety form
export const createHealthSafetyForm = createAsyncThunk(
  "healthSafetyForm/create",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post("/health-safety", data);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || "Failed to create Health & Safety form"
      );
    }
  }
);

// Update Health Safety form
export const updateHealthSafetyForm = createAsyncThunk(
  "healthSafetyForm/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/health-safety/${id}`, data);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || "Failed to update Health & Safety form"
      );
    }
  }
);

// Delete Health Safety form
export const deleteHealthSafetyForm = createAsyncThunk(
  "healthSafetyForm/delete",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/health-safety/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to delete Health & Safety form"
      );
    }
  }
);

// ==================== Slice ====================
const healthSafetyFormSlice = createSlice({
  name: "healthSafetyForm",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentForm: (state, action) => {
      state.currentForm = action.payload;
    },
    clearCurrentForm: (state) => {
      state.currentForm = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchHealthSafetyForms.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchHealthSafetyForms.fulfilled, (state, action) => {
        state.isLoading = false;
        state.healthSafetyForms = action.payload;
      })
      .addCase(fetchHealthSafetyForms.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH ONE
      .addCase(fetchHealthSafetyForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchHealthSafetyForm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentForm = action.payload;
      })
      .addCase(fetchHealthSafetyForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createHealthSafetyForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createHealthSafetyForm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.healthSafetyForms.unshift(action.payload);
      })
      .addCase(createHealthSafetyForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateHealthSafetyForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateHealthSafetyForm.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data ?? action.payload;

        const index = state.healthSafetyForms.findIndex(
          (f) => f.id === updated.id
        );
        if (index !== -1) {
          state.healthSafetyForms[index] = updated;
        }

        if (state.currentForm?.id === updated.id) {
          state.currentForm = updated;
        }
      })
      .addCase(updateHealthSafetyForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteHealthSafetyForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteHealthSafetyForm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.healthSafetyForms = state.healthSafetyForms.filter(
          (f) => f.id !== action.payload
        );

        if (state.currentForm?.id === action.payload) {
          state.currentForm = null;
        }
      })
      .addCase(deleteHealthSafetyForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setCurrentForm, clearCurrentForm } =
  healthSafetyFormSlice.actions;

export default healthSafetyFormSlice.reducer;
